/* =========================================================================
 * Price alerts — market service model
 *
 * The market service serialises every enum as its full string name
 * (`PRICE_ALERT_STATUS_ACTIVE`), never as a number. The DTOs keep those fields
 * as plain strings and map through the helpers below, so an unknown value added
 * by the backend degrades to `null` instead of breaking the screen.
 * ========================================================================= */

export enum PriceAlertCondition {
  /** Fires when the OTC buy price reaches the target or goes above it. */
  ABOVE_OR_EQUAL = 'PRICE_ALERT_CONDITION_ABOVE_OR_EQUAL',
  /** Fires when the OTC buy price reaches the target or drops below it. */
  BELOW_OR_EQUAL = 'PRICE_ALERT_CONDITION_BELOW_OR_EQUAL'
}

/** INBOX exists server-side but is internal and never listed. */
export enum PriceAlertChannel {
  EMAIL = 'PRICE_ALERT_CHANNEL_EMAIL',
  PUSH = 'PRICE_ALERT_CHANNEL_PUSH'
}

export enum PriceAlertStatus {
  ACTIVE = 'PRICE_ALERT_STATUS_ACTIVE',
  /** Terminal: the alert fired (its last time, for a repeating alert). */
  TRIGGERED = 'PRICE_ALERT_STATUS_TRIGGERED',
  /** Terminal: cancelled by the user **or** expired — compare `expiresAt` to tell them apart. */
  CANCELED = 'PRICE_ALERT_STATUS_CANCELED',
  /** Request-side only: "no filter". */
  UNSPECIFIED = 'PRICE_ALERT_STATUS_UNSPECIFIED'
}

export enum PriceAlertRepeatMode {
  /** Fires once, then becomes TRIGGERED. */
  SINGLE = 'PRICE_ALERT_REPEAT_MODE_SINGLE',
  /** Fires up to MAX_REPEAT_TRIGGERS times, re-arming between crossings. */
  REPEAT = 'PRICE_ALERT_REPEAT_MODE_REPEAT'
}

const isKnown = <T extends Record<string, string>>(
  values: T,
  value?: string | null
): value is T[keyof T] =>
  !!value && Object.values(values).includes(value as T[keyof T]);

export const toCondition = (
  value?: string | null
): PriceAlertCondition | null =>
  isKnown(PriceAlertCondition, value) ? value : null;

export const toStatus = (value?: string | null): PriceAlertStatus | null =>
  isKnown(PriceAlertStatus, value) ? value : null;

export const toRepeatMode = (
  value?: string | null
): PriceAlertRepeatMode | null =>
  isKnown(PriceAlertRepeatMode, value) ? value : null;

export const toChannels = (values?: string[] | null): PriceAlertChannel[] =>
  (values || []).filter((value): value is PriceAlertChannel =>
    isKnown(PriceAlertChannel, value)
  );

/* =========================
 * Alert
 * ========================= */

/**
 * Responses are camelCase and emit zero values rather than omitting keys, so
 * `triggeredPrice` arrives as `""` and `triggerCount` as `0` before the first firing.
 */
export interface PriceAlertItem {
  id: string;
  symbol: string;
  /** One of PriceAlertCondition. */
  condition: string;
  /** Decimal string — never parse into a number, IRT targets lose precision. */
  targetPrice: string;
  /** The channels the user chose. INBOX is internal and never listed. */
  channels: string[];
  /** One of PriceAlertStatus. */
  status: string;
  /** Price at the most recent firing; `""` until the alert has fired. */
  triggeredPrice: string;
  /** Time of the most recent firing; absent until the alert has fired. */
  triggeredAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** `createdAt` + 3 calendar months, fixed at creation and never extended by an edit. */
  expiresAt: string;
  /** One of PriceAlertRepeatMode. */
  repeatMode: string;
  /** How many times the alert has fired, out of MAX_REPEAT_TRIGGERS for a repeating alert. */
  triggerCount: number;
  /** `false` only for a REPEAT alert waiting for the price to leave the target zone. */
  armed: boolean;
}

/* =========================
 * Requests
 * ========================= */

/** `POST market/price-alert` */
export interface CreatePriceAlertRequest {
  symbol: string;
  condition: PriceAlertCondition;
  /** Decimal string, must be > 0. Send what the user typed, normalised. */
  target_price: string;
  /** Non-empty, no duplicates, never INBOX. */
  channels: PriceAlertChannel[];
  /** Omitted means SINGLE. */
  repeat_mode?: PriceAlertRepeatMode;
}

export interface PaginationRequest {
  offset: number;
  limit: number;
  desc?: boolean;
}

/**
 * `POST market/price-alert/list` — every field optional, combined with AND.
 * An empty body returns the first page of everything.
 */
export interface GetPriceAlertListRequest {
  /** Fetches a single alert; there is no `GET /price-alert/{id}`. */
  id?: string;
  symbol?: string;
  /** Omit or UNSPECIFIED for any status. */
  status_filter?: PriceAlertStatus;
  pagination?: PaginationRequest;
}

/**
 * `PATCH market/price-alert/{id}` — edit **or** cancel.
 *
 * At least one field must be present. Cancelling cannot be combined with
 * `target_price` or `add_channels`; channels can only be added, never removed.
 */
export interface UpdatePriceAlertRequest {
  id: string;
  target_price?: string;
  /** Newly added channels consume daily quota. */
  add_channels?: PriceAlertChannel[];
  /** The only status a client may set is CANCELED. */
  status?: typeof PriceAlertStatus.CANCELED;
}

/* =========================
 * Responses
 * ========================= */

export interface PriceAlertResponse {
  data: PriceAlertItem;
}

export interface GetPriceAlertListResponse {
  data: {
    items: PriceAlertItem[];
    /** Count matching the filter, ignoring pagination — use it for the pager. */
    total: number;
  };
}

/* =========================
 * View model
 * ========================= */

export interface PriceAlertRowData extends PriceAlertItem {
  // Market information, merged in from the market list
  baseFa: string;
  quoteFa: string;
  baseEn: string;
  quoteEn: string;
  lastPrice: number;
  priceChangePercent: number;
  basePrecision: number;
  // Derived from the raw enum strings
  conditionType: PriceAlertCondition | null;
  statusType: PriceAlertStatus | null;
  repeatModeType: PriceAlertRepeatMode | null;
  channelTypes: PriceAlertChannel[];
  isActive: boolean;
  /** An active alert that already fired and is waiting for the price to move away. */
  isWaitingToReArm: boolean;
  /**
   * There is no EXPIRED status — expiry silently reuses CANCELED, so the only way
   * to tell an expired alert from a user-cancelled one is to compare `expiresAt` with now.
   */
  isExpired: boolean;
  /** Terminal alerts can no longer be edited or cancelled. */
  isTerminal: boolean;
}
